import { Box, IconButton, Popover, useTheme } from '@mui/material';
import { format, isValid } from 'date-fns';
import { FilterAdd, FilterEdit } from 'iconsax-react';
import { type MouseEvent, type ReactElement, useEffect, useState } from 'react';

import Enums from '@core/types/api/Enums';
import { hasValue } from '@core/utils/equality';

import { useDebouncedEffect } from '../../../hooks/useDebouncedEffect';
import type { FilterType, NamedFilterOpts } from '../../../utils/ApiClient';
import type { Column } from '../TableDataModel';
import { DateTimeColumnFilter } from './DateTimeColumnFilter';
import { InColumnFilter } from './InColumnFilter';
import { NumericColumnFilter } from './NumericColumnFilter';
import { StringColumnFilterSearch } from './StringColumnFilterSearch';
import type { TypeAheadTables } from './types';

interface TableFilterProps<TRow extends { id: string }> {
  typeaheadTableName?: TypeAheadTables;
  column: Column<TRow>;
  filter?: NamedFilterOpts<TRow> | null;
  onFilterChange?: (column: Column<TRow>, filter: NamedFilterOpts<TRow> | null) => void;
  onFilterRemove?: (column: Column<TRow>) => void;
}

export const supportedColumnFilterOperations: ReadonlyArray<Enums.FilterOperation> = [
  Enums.FilterOperation.In,
  Enums.FilterOperation.GreaterOrEquals,
  Enums.FilterOperation.LessOrEquals,
  Enums.FilterOperation.Between,
  Enums.FilterOperation.Contains,
] as const;

export function ColumnFilter<TRow extends { id: string }>({
  typeaheadTableName,
  column,
  filter,
  onFilterChange,
  onFilterRemove,
}: TableFilterProps<TRow>): ReactElement {
  const [filteredValues, setFilteredValues] = useState<FilterType[]>([]);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const theme = useTheme();

  const isNumericColumn =
    column.type === 'number' || column.type === 'Cost' || column.type === 'Currency';

  const isStringColumn = column.type === 'string';
  const isDateTimeColumn = column.type === 'DateTime';

  const isInColumn = column?.isTypeahead === true;

  useEffect(() => {
    if (filter === null || filter === undefined) {
      setFilteredValues(['', '']);
      return;
    }

    if (filter.operation === Enums.FilterOperation.In) {
      setFilteredValues([...filter.values]);
      return;
    }

    // A text filter saved before the column became a picker must not join its selection.
    if (isInColumn) {
      setFilteredValues(['', '']);
      return;
    }

    if (isStringColumn) {
      setFilteredValues([filter.values?.[0] ?? '', '']);
      return;
    }

    const val1 = filter.values?.[0] ?? '';
    const val2 = filter.values?.[1] ?? '';

    if (filter.operation === Enums.FilterOperation.GreaterOrEquals) {
      setFilteredValues([val1, '']);
    } else if (filter.operation === Enums.FilterOperation.LessOrEquals) {
      setFilteredValues(['', val1]);
    } else {
      setFilteredValues([val1, val2]);
    }
  }, [filter?.values[0], filter?.values[1], filter?.operation]);

  const handleRangeChange = (lowerValue: FilterType, higherValue: FilterType): void => {
    let formattedLowerValue = '';
    let formattedHigherValue = '';

    if (column.type === 'DateTime') {
      formattedLowerValue =
        lowerValue !== null && lowerValue !== undefined && isValid(lowerValue)
          ? format(Number(lowerValue), 'yyyy-MM-dd')
          : '';
      formattedHigherValue =
        higherValue !== null && higherValue !== undefined && isValid(higherValue)
          ? format(Number(higherValue), 'yyyy-MM-dd')
          : '';
    } else if (isNumericColumn) {
      formattedLowerValue = hasValue(lowerValue) ? String(lowerValue) : '';
      formattedHigherValue = hasValue(higherValue) ? String(higherValue) : '';
    }

    const updatedValues: [string, string] = [formattedLowerValue, formattedHigherValue];

    const [fromValue, toValue] = updatedValues;
    let newOperation: Enums.FilterOperation | null = null;
    let valuesToSend: string[] = [];

    if (fromValue !== '' && toValue !== '') {
      newOperation = Enums.FilterOperation.Between;
      valuesToSend = [fromValue, toValue];
    } else if (fromValue !== '') {
      newOperation = Enums.FilterOperation.GreaterOrEquals;
      valuesToSend = [fromValue];
    } else if (toValue !== '') {
      newOperation = Enums.FilterOperation.LessOrEquals;
      valuesToSend = [toValue];
    }

    if (newOperation !== null) {
      onFilterChange?.(column, {
        name: column.id,
        operation: newOperation,
        values: valuesToSend,
      });
    }
  };

  const handleSingleValueChange = (value: FilterType): void => {
    if (hasValue(value) && value !== '') {
      onFilterChange?.(column, {
        name: column.id,
        operation: Enums.FilterOperation.Contains,
        values: [value],
      });
    } else {
      onFilterChange?.(column, null);
    }
  };

  const handleInOptionChange = (option: string, checked: boolean): void => {
    const currentValues = [...filteredValues.filter(Boolean)];

    if (checked) {
      if (!currentValues.includes(option)) {
        currentValues.push(option);
      }
    } else {
      const index = currentValues.indexOf(option);
      if (index !== -1) {
        currentValues.splice(index, 1);
      }
    }

    setFilteredValues(currentValues);

    if (currentValues.length > 0) {
      onFilterChange?.(column, {
        name: column.id,
        operation: Enums.FilterOperation.In,
        values: currentValues,
      });
    } else {
      onFilterChange?.(column, null);
      onFilterRemove?.(column);
    }
  };

  const handleStringFilterChange = (value: FilterType): void => {
    setFilteredValues([value, '']);
    if (value === '') {
      onFilterRemove?.(column);
    }
  };

  const handleNumericFilterChange = (values: FilterType[]): void => {
    setFilteredValues(values);
    if (values[0] === '' && values[1] === '') {
      onFilterRemove?.(column);
    }
  };

  const handleDateTimeFilterChange = (values: FilterType[]): void => {
    setFilteredValues(values);
    if (values[0] === undefined && values[1] === undefined) {
      onFilterRemove?.(column);
    }
  };

  const handleFilterClose = (): void => {
    setAnchorEl(null);
  };

  const handleFilterClear = (): void => {
    setFilteredValues(['', '']);
    onFilterRemove?.(column);
    handleFilterClose();
  };

  const handleClickFilterIconClick = (event: MouseEvent<HTMLElement>): void => {
    setAnchorEl(event.currentTarget);
  };

  useDebouncedEffect(
    () => {
      if (isInColumn) {
        return;
      }

      if (isNumericColumn || isDateTimeColumn) {
        handleRangeChange(filteredValues[0], filteredValues[1]);
      } else if (isStringColumn) {
        handleSingleValueChange(filteredValues[0]);
      }
    },
    [filteredValues],
    500,
  );

  return (
    <>
      <IconButton onClick={handleClickFilterIconClick}>
        {filter !== null && filter !== undefined ? (
          <FilterEdit variant='TwoTone' color={theme.palette.SlabGreen[800]} />
        ) : (
          <FilterAdd variant='TwoTone' color={theme.palette.primary.main} />
        )}
      </IconButton>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleFilterClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <Box p={2} width={250}>
          {isDateTimeColumn && (
            <DateTimeColumnFilter
              filterValues={filteredValues}
              onFilterChange={handleDateTimeFilterChange}
              onFilterClear={handleFilterClear}
            />
          )}

          {isNumericColumn && (
            <NumericColumnFilter
              filterValues={filteredValues}
              onFilterChange={handleNumericFilterChange}
              onFilterClear={handleFilterClear}
            />
          )}

          {isInColumn && typeaheadTableName !== undefined && (
            <InColumnFilter
              filterValues={filteredValues}
              onFilterChange={handleInOptionChange}
              onFilterClear={handleFilterClear}
              isFilterOpen={anchorEl !== null}
              typeaheadTableName={typeaheadTableName}
              column={column}
            />
          )}

          {isStringColumn && !isInColumn && (
            <StringColumnFilterSearch
              filterValue={filteredValues[0]}
              onFilterChange={handleStringFilterChange}
              onFilterClear={handleFilterClear}
            />
          )}
        </Box>
      </Popover>
    </>
  );
}
